import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Card } from '../../components/ui'
import { Users, Trophy, Building, Calendar, Activity, Database, Server, ShieldCheck, Mail } from 'lucide-react'

export const PlatformDashboard = () => {
    const [stats, setStats] = useState({
        users: 0,
        tenants: 0,
        tournaments: 0,
        matches: 0,
        teams: 0
    })
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchStats()
    }, [])

    const fetchStats = async () => {
        try {
            // Fetch counts (using head: true to only get count, not data)
            const [
                { count: usersCount },
                { count: tenantsCount },
                { count: tournamentsCount },
                { count: matchesCount },
                { count: teamsCount }
            ] = await Promise.all([
                supabase.from('profiles').select('*', { count: 'exact', head: true }),
                supabase.from('tenants').select('*', { count: 'exact', head: true }),
                supabase.from('tournaments').select('*', { count: 'exact', head: true }),
                supabase.from('matches').select('*', { count: 'exact', head: true }),
                supabase.from('teams').select('*', { count: 'exact', head: true })
            ])

            setStats({
                users: usersCount || 0,
                tenants: tenantsCount || 0,
                tournaments: tournamentsCount || 0,
                matches: matchesCount || 0,
                teams: teamsCount || 0
            })
        } catch (error) {
            console.error('Error fetching platform stats:', error)
        } finally {
            setLoading(false)
        }
    }

    const StatCard = ({ label, value, icon: Icon, color }) => (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
                <p className="text-gray-500 text-sm font-medium mb-1">{label}</p>
                <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
            </div>
            <div className={`p-4 rounded-xl ${color}`}>
                <Icon size={24} className="text-white" />
            </div>
        </div>
    )

    return (
        <div className="p-8 max-w-7xl mx-auto min-h-screen bg-gray-50/50">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                    <Activity className="text-purple-600" />
                    Systeem Analytics
                </h1>
                <p className="text-gray-500">Live data overzicht van het ProPoule platform</p>
            </div>

            {/* Main Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    label="Totaal Gebruikers"
                    value={stats.users}
                    icon={Users}
                    color="bg-blue-500"
                />
                <StatCard
                    label="Totaal Tenants"
                    value={stats.tenants}
                    icon={Building}
                    color="bg-purple-500"
                />
                <StatCard
                    label="Totaal Toernooien"
                    value={stats.tournaments}
                    icon={Trophy}
                    color="bg-orange-500"
                />
                <StatCard
                    label="Totaal Wedstrijden"
                    value={stats.matches}
                    icon={Calendar}
                    color="bg-green-500"
                />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                        <Users className="text-gray-400" />
                        <h3 className="font-bold text-gray-700">Totaal Teams</h3>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">{stats.teams}</p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                        <Calendar className="text-gray-400" />
                        <h3 className="font-bold text-gray-700">Wedstrijden Vandaag</h3>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">0</p>
                    <p className="text-xs text-gray-400 mt-2">Nog niet geïmplementeerd</p>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                        <Trophy className="text-gray-400" />
                        <h3 className="font-bold text-gray-700">Actieve Toernooien</h3>
                    </div>
                    <p className="text-4xl font-bold text-gray-900">0</p>
                    <p className="text-xs text-green-500 mt-2">● Live</p>
                </div>
            </div>

            {/* Security & System Check */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                        <ShieldCheck className="text-green-600" />
                        Security & Monitoring
                    </h3>
                </div>
                <div className="p-0">
                    <div className="grid grid-cols-1 divide-y">
                        <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                                <Database size={18} className="text-gray-400" />
                                <span className="text-gray-700">Database Connectie</span>
                            </div>
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">ACTIEF</span>
                        </div>
                        <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                                <Server size={18} className="text-gray-400" />
                                <span className="text-gray-700">RLS Policies</span>
                            </div>
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">HANDHAVEN</span>
                        </div>
                        <div className="p-4 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                                <Mail size={18} className="text-gray-400" />
                                <span className="text-gray-700">Email Service</span>
                            </div>
                            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">GEREED</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recommendations */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2">
                    ⚡ Systeem Aanbevelingen
                </h3>
                <ul className="space-y-2 text-sm text-amber-800">
                    <li className="flex items-start gap-2">
                        <span className="mt-1">•</span>
                        <span>Stel budget alerts in via Supabase Dashboard om kosten te bewaken.</span>
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="mt-1">•</span>
                        <span>Controleer regelmatig de error logs in de SQL Editor.</span>
                    </li>
                </ul>
            </div>
        </div>
    )
}
